import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ClientLink from '../components/ClientLink';
import LoanLink from '../components/LoanLink';
import { openDocument } from '../utils/documentLinks';
import { ShieldAlert, Search, FileWarning, CheckCircle2, XCircle, RotateCcw, Eye } from 'lucide-react';
import StatusBadge from '../components/UI/StatusBadge';
import { formatDateDMY, formatDateTimeDMY } from '../utils/dateUtils';

// Documents flagged as possibly tampered with, edited or AI-generated
// (GET /api/document-flags). Every upload is checked; these are the ones that
// showed enough warning signs to need a person to look. Staff open the
// document, then record the outcome: Genuine (false alarm) or Tampered.
const RISK_STYLES = {
  high: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800/50',
  medium: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800/50',
};
const SOURCE_LABELS = {
  registration: 'Online registration',
  registration_update: 'Registration update',
  prequalification: 'Pre-qualification',
  loan_application: 'Loan application',
  document_upload: 'Document upload',
};

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DocumentFlags = () => {
  const [status, setStatus] = useState('Open');
  const [risk, setRisk] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ status });
      if (risk) params.set('risk', risk);
      if (q.trim()) params.set('q', q.trim());
      const res = await axios.get(apiUrl(`/api/document-flags?${params}`), auth());
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Flagged documents could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [status, risk, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const review = async (row, next) => {
    setBusyId(row.id);
    setMessage('');
    try {
      await axios.patch(apiUrl(`/api/document-flags/${row.id}`), { status: next, note: notes[row.id] ?? undefined }, auth());
      setMessage(next === 'Open' ? 'Flag reopened.' : `Marked ${next === 'Genuine' ? 'genuine' : 'tampered'}.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'The review could not be saved.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-full min-h-screen">
      <div className="flex items-center gap-3 mb-1">
        <ShieldAlert className="text-rose-600 dark:text-rose-400" size={26} />
        <h1 className="text-2xl sm:text-3xl font-extrabold text-blue-800 dark:text-blue-300">Flagged Documents</h1>
      </div>
      <p className="text-sm text-gray-600 dark:text-slate-400 mb-4 max-w-3xl">
        Documents that showed signs of being edited, tampered with or generated. Every upload is checked automatically;
        these are warning signs, not proof — open the document, compare it with what you know, then record the outcome.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {['Open', 'Tampered', 'Genuine', 'all'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              status === s
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700'
            }`}
          >
            {s === 'all' ? 'All' : s === 'Open' ? 'To review' : s}
          </button>
        ))}
        <select
          value={risk}
          onChange={(e) => setRisk(e.target.value)}
          
        >
          <option value="">Any risk</option>
          <option value="high">High risk</option>
          <option value="medium">Medium risk</option>
        </select>
        <div className="relative flex-1 min-w-[12rem] max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Client, code, file or type"
            className="w-full pl-8 pr-3"
          />
        </div>
      </div>

      {message && <div className="mb-3 p-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm dark:bg-emerald-900/20 dark:border-emerald-800/40 dark:text-emerald-300">{message}</div>}
      {error && <div className="mb-3 p-2.5 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm dark:bg-rose-900/20 dark:border-rose-800/40 dark:text-rose-300">{error}</div>}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-slate-600 p-8 text-center text-sm text-gray-500 dark:text-slate-400">
          <CheckCircle2 className="mx-auto mb-2 text-emerald-500" size={28} />
          {status === 'Open' ? 'No documents waiting for review.' : 'No flagged documents match.'}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.id} className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
              <div className="p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-rose-50 dark:bg-rose-900/30 flex items-center justify-center flex-shrink-0">
                    <FileWarning className="text-rose-600 dark:text-rose-400" size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                      {row.document_type || 'Document'}
                      <span className="font-normal text-gray-500 dark:text-slate-400"> — <ClientLink clientId={row.client_id} name={row.client_name || 'Unknown client'} />{row.client_code ? ` (${row.client_code})` : ''}</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                      {row.file_name} · {SOURCE_LABELS[row.source] || row.source}
                      {row.loan_code ? <> · loan <LoanLink loanId={row.loan_id}>{row.loan_code}</LoanLink></> : ''} · {formatDateTimeDMY(row.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${RISK_STYLES[row.risk_level] || RISK_STYLES.medium}`}>
                    {row.risk_level === 'high' ? 'High risk' : 'Medium risk'} · {row.score}/100
                  </span>
                  <StatusBadge status={row.status} label={row.status === 'Open' ? 'To review' : row.status} />
                </div>
              </div>

              <ul className="px-4 pb-3 space-y-1">
                {(row.signals || []).map((s, i) => (
                  <li key={`${s.code}-${i}`} className="text-xs text-gray-700 dark:text-slate-300 flex gap-2">
                    <span className="text-rose-500">•</span>
                    <span>{s.message}</span>
                  </li>
                ))}
              </ul>

              <div className="px-4 py-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/30 flex flex-wrap items-center gap-2">
                {row.document_id ? (
                  <button type="button" onClick={() => openDocument(row.document_id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-700">
                    <Eye size={14} /> Open document
                  </button>
                ) : (
                  <span className="text-xs text-gray-500 dark:text-slate-400">Document not stored separately</span>
                )}
                <input
                  value={notes[row.id] ?? row.review_note ?? ''}
                  onChange={(e) => setNotes((n) => ({ ...n, [row.id]: e.target.value }))}
                  placeholder="Review note (optional)"
                  className="flex-1 min-w-[12rem]"
                />
                {row.status === 'Open' ? (
                  <>
                    <button type="button" disabled={busyId === row.id} onClick={() => review(row, 'Genuine')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                      <CheckCircle2 size={14} /> Looks genuine
                    </button>
                    <button type="button" disabled={busyId === row.id} onClick={() => review(row, 'Tampered')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
                      <XCircle size={14} /> Confirm tampered
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-gray-500 dark:text-slate-400">
                      Reviewed{row.reviewed_by_email ? ` by ${row.reviewed_by_email}` : ''}{row.reviewed_at ? ` on ${formatDateDMY(row.reviewed_at)}` : ''}
                    </span>
                    <button type="button" disabled={busyId === row.id} onClick={() => review(row, 'Open')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50">
                      <RotateCcw size={14} /> Reopen
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentFlags;
